import { Controller } from "@hotwired/stimulus"
import blueCrossIcon from "../../assets/images/blue-cross.svg"

export default class extends Controller {
  static targets = ["menu", "submenu", "arrow", "selected", "preselected", "removedUsers"];

  connect() {
    const shownMenuId = localStorage.getItem("shownDropdownId");
    if (shownMenuId && this.menuTarget.id === shownMenuId) {
      this.menuTarget.removeAttribute("hidden");
      document.addEventListener("click", this.closeDropdowns);
      if (this.triggerElement) this.triggerElement.setAttribute("aria-expanded", "true");
      this.restoreFocus(this.menuTarget);
    }
    this.changeBackground();
    this.handleKeydown = this.handleKeydown.bind(this);
    this.element.addEventListener("keydown", this.handleKeydown);

    if (this.hasPreselectedTarget) {
      const items = this.preselectedTarget.querySelectorAll("[data-user-id]")
      
      items.forEach(item => {
        const userId = item.dataset.userId
        const userName = item.dataset.userName

        const checkbox = this.menuTarget.querySelector(`input[type='checkbox'][value='${userId}']`)
        if (checkbox) checkbox.checked = true

        const existing = this.selectedTarget.querySelector(`[data-user-id='${userId}']`)
        if (!existing) {
          const div = document.createElement("div")
          div.dataset.userId = userId
          div.className = "selected-user"
          div.innerHTML = `
            <img src="${blueCrossIcon}"
                alt="Remove"
                data-user-id="${userId}"
                data-action="click->dropdown#removeUser"
                class="remove-user-icon" />
            <span class="selected-user-name">${userName}</span>
          `
          this.selectedTarget.appendChild(div)
        }
      })
    }
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener("click", this.closeDropdowns);
  }

  restoreFocus(menu) {
    const focusFor = localStorage.getItem("shownDropdownFocusFor");
    localStorage.removeItem("shownDropdownFocusFor");

    const focusTarget = focusFor
      ? menu.querySelector(`label[for="${focusFor}"]`)
      : null;

    const fallback = menu.querySelector('input[type="text"].filter');
    const element = focusTarget || fallback;
    if (!element) return;

    this.ensureFocusable(element);
    setTimeout(() => element.focus(), 0);
  }

  ensureFocusable(element) {
    if (element.tagName === "LABEL" && !element.hasAttribute("tabindex")) {
      element.setAttribute("tabindex", "-1");
    }
  }

  get triggerElement() {
    return this.element.querySelector("button.hotwire-button, [role='button']");
  }

  handleKeydown(event) {
    if (this.menuTarget.hasAttribute("hidden")) return;

    if (event.key === "Escape") {
      this.close();
      event.preventDefault();
      return;
    }

    if (event.key === "Enter" || (event.key === " " && event.target.closest('.label-for-check'))) {
      const label = event.target.closest('.label-for-check');
      const checkbox = label
        ? document.getElementById(label.getAttribute("for"))
        : (event.target.type === "checkbox" ? event.target : null);

      if (checkbox) {
        event.preventDefault();
        if (label) localStorage.setItem("shownDropdownFocusFor", label.getAttribute("for"));
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
        return;
      }
    }

    if (event.key === "Tab") {
      this.trapFocus(event);
    }
  }

  trapFocus(event) {
    const focusable = Array.from(this.menuTarget.querySelectorAll(
      'input[type="text"].filter, .label-for-check'
    ));
    if (focusable.length === 0) return;

    const current = event.target.closest('.label-for-check') || event.target;
    const currentIndex = focusable.indexOf(current);

    let nextIndex;
    if (event.shiftKey) {
      nextIndex = currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex >= focusable.length - 1 ? 0 : currentIndex + 1;
    }

    const target = focusable[nextIndex];
    this.ensureFocusable(target);
    target.focus();
    event.preventDefault();
  }

  close() {
    this.menuTarget.setAttribute("hidden", "");
    localStorage.removeItem("shownDropdownId");
    localStorage.removeItem("shownDropdownFocusFor");
    if (this.hasArrowTarget) {
      this.arrowTarget.classList.remove("flipped");
    }
    if (this.hasSubmenuTarget) {
      this.submenuTarget.setAttribute("hidden", "");
    }
    if (this.triggerElement) {
      this.triggerElement.setAttribute("aria-expanded", "false");
      this.triggerElement.focus();
    }
    document.removeEventListener("click", this.closeDropdowns);
  }

  changeBackground() {
    const checkboxes = this.element.querySelectorAll('input[type="checkbox"]');
    const button = this.element.querySelector('.hotwire-button');
    if (!button) { return null; }
    const isChecked = Array.from(checkboxes).some(checkbox => checkbox.checked);
    if (isChecked) {
      button.style.backgroundColor = '#D9D9D9';
    } else {
      button.style.backgroundColor = 'white';
    }
  }

  toggle(event) {
    event.preventDefault();
    const currentMenu = this.menuTarget;
    const allMenus = document.querySelectorAll(".dropdown-list:not(.select-student-dropdown)");
    allMenus.forEach((menu) => {
      if (menu !== currentMenu) {
        menu.setAttribute("hidden", "");
      }
    });
    if (currentMenu.hasAttribute("hidden")) {
      if (this.hasArrowTarget) {
        this.arrowTarget.classList.add("flipped");
      }
      currentMenu.removeAttribute("hidden");
      localStorage.setItem("shownDropdownId", currentMenu.id);
      if (this.triggerElement) this.triggerElement.setAttribute("aria-expanded", "true");
      document.addEventListener("click", this.closeDropdowns);
      const searchInput = currentMenu.querySelector('input[type="text"].filter');
      if (searchInput) {
        requestAnimationFrame(() => searchInput.focus());
      } else {
        const firstCheckbox = currentMenu.querySelector('input[type="checkbox"]');
        if (firstCheckbox) requestAnimationFrame(() => firstCheckbox.focus());
      }
    } else {
      this.close();
    }
    this.changeBackground();
  }

  toggleSubmenu(event) {
      event.preventDefault();
      if (this.hasSubmenuTarget) { // Check if submenuTarget exists
          const submenu = this.submenuTarget;
          const allSubmenus = document.querySelectorAll("[data-target='dropdown.submenu']");
          allSubmenus.forEach((sub) => {
              if (sub !== submenu) {
                  sub.setAttribute("hidden", "");
              }
          });
          if (submenu.hasAttribute("hidden")) {
              submenu.removeAttribute("hidden");
          } else {
              submenu.setAttribute("hidden", "");
          }
      }
  }

  closeDropdowns = (event) => {
      if (!event.target.closest("[data-controller='dropdown']")) {
          this.close();
      }
  }

  toggleOnEnter(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      this.toggle(event)
    }
  }

  toggleEducatorDashboardSort(event){
    if (this.hasArrowTarget) {
      this.arrowTarget.classList.toggle("flipped");
    }
    if (!this.menuTarget.classList.contains("hidden")) {
        document.addEventListener("click", this.closeDropdowns)
    } else {
        document.removeEventListener("click", this.closeDropdowns)
    }
    const currentMenu = this.menuTarget;
    const allMenus = document.querySelectorAll(".dropdown-list");
    allMenus.forEach((menu) => {
        if (menu !== currentMenu) {
            menu.setAttribute("hidden", "");
        }
    });
    if (currentMenu.hasAttribute("hidden")) {
        currentMenu.removeAttribute("hidden");
        localStorage.setItem("shownDropdownId", currentMenu.id);
    } else {
        currentMenu.setAttribute("hidden", "");
        localStorage.removeItem("shownDropdownId");
    }
    this.changeBackground();
  }

  toggleUser(event) {
    const checkbox = event.target
    const userId = checkbox.value
    const userName = checkbox.dataset.name
    const existing = this.selectedTarget.querySelector(`[data-user-id='${userId}']`)

    if (checkbox.checked) {
      if (!existing) {
        const div = document.createElement("div")
        div.dataset.userId = userId
        div.className = "selected-user"
        div.innerHTML = `
          <img src="${blueCrossIcon}"
              alt="Remove"
              data-user-id="${userId}"
              data-action="click->dropdown#removeUser"
              class="remove-user-icon" />
          <span class="selected-user-name">${userName}</span>
        `
        this.selectedTarget.appendChild(div)
      }
    } else {
      if (existing) existing.remove()
    }
  }

  removeUser(event) {
    const userId = event.target.dataset.userId;

    const checkbox = this.menuTarget.querySelector(`input[type='checkbox'][value='${userId}']`);
    if (checkbox) checkbox.checked = false;

    const userDiv = this.selectedTarget.querySelector(`[data-user-id='${userId}']`);
    if (userDiv) userDiv.remove();

    const existingHidden = this.removedUsersTarget?.querySelector(`input[value='${userId}']`);
    if (!existingHidden && this.hasRemovedUsersTarget) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "users_to_remove[]";
      input.value = userId;
      this.removedUsersTarget.appendChild(input);
    }
  }
}